type HiddenSlidePage = {
  pageId?: string,
  number: number,
  isRequired?: boolean | null,
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
  linkedSlides?: string[] | null,
}

type HiddenSlidePageSetting = {
  pageId?: string,
  number: number,
  isRequired?: boolean | null,
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
  linkedSlides?: string[] | null,
}

export type HiddenSlidePageSettingOverride = {
  pageId: string,
  hidden?: boolean,
}

export type HiddenSlideContentPageOverrideTarget = {
  pageId?: string | null,
  documentPageNumber: number,
  presentationPageNumber?: number,
}

type HiddenSlideContentPage = {
  pageId?: string,
  documentPageNumber: number,
  presentationPageNumber?: number,
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
}

export function isSourceHiddenSlide(page: {
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
}): boolean {
  // Legacy data may only have isHidden, but new flows keep source provenance separately.
  return !!page.sourceIsHidden || !!page.isHidden;
}

export function getHiddenSlidePageStateByNumber<TPage extends {
  number: number,
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
}>({
  pages,
  pageNumber,
}: {
  pages?: TPage[],
  pageNumber?: number,
}): {
  isHidden?: boolean,
  sourceIsHidden?: boolean,
} | undefined {
  if (!pageNumber) {
    return undefined;
  }

  const page = pages?.find(({ number }) => number === pageNumber);
  if (!page) {
    return undefined;
  }

  return {
    isHidden: !!page.isHidden,
    sourceIsHidden: !!page.sourceIsHidden,
  };
}

export function canToggleHiddenSlide(page: {
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
}): boolean {
  return !!page.sourceIsHidden;
}

export function getSlideRollHiddenSlideMode({
  featureEnabled,
  canToggleHidden,
}: {
  featureEnabled: boolean,
  canToggleHidden?: boolean,
}): {
  showHiddenSlidesInSlideRule: boolean,
  enableHiddenSlideToggle: boolean,
} {
  return {
    showHiddenSlidesInSlideRule: featureEnabled,
    enableHiddenSlideToggle: featureEnabled && !!canToggleHidden,
  };
}

function getPageWithHiddenMetadata<
  TPage extends {
    isHidden?: boolean | null,
    sourceIsHidden?: boolean | null,
  },
>(
  page: TPage,
  source?: {
    isHidden?: boolean | null,
    sourceIsHidden?: boolean | null,
  },
): TPage {
  if (!source || !isSourceHiddenSlide(source)) {
    return page;
  }

  return {
    ...page,
    isHidden: typeof source.isHidden === 'boolean'
      ? source.isHidden
      : !!page.isHidden,
    sourceIsHidden: !!source.sourceIsHidden || !!page.sourceIsHidden,
  };
}

function getSlideRollDisplayPage<
  TPage extends {
    isHidden?: boolean | null,
    sourceIsHidden?: boolean | null,
  },
>({
  page,
  source,
  includeHiddenSlides,
}: {
  page: TPage,
  source?: {
    isHidden?: boolean | null,
    sourceIsHidden?: boolean | null,
  },
  includeHiddenSlides?: boolean,
}): TPage {
  if (!includeHiddenSlides) {
    return page;
  }

  return getPageWithHiddenMetadata(page, source);
}

export function getHiddenSlideNavigationPages(pages: HiddenSlidePage[]): number[] {
  return pages.reduce<number[]>((acc, page) => {
    if (!page.isHidden) {
      acc.push(page.number);
    }

    return acc;
  }, []);
}

function findHiddenSlideContentPage(
  page: HiddenSlidePage,
  presentationPageNumber: number,
  contentPages?: HiddenSlideContentPage[],
): HiddenSlideContentPage | undefined {
  return contentPages?.find((content) => {
    if (content.pageId && page.pageId && content.pageId === page.pageId) {
      return true;
    }

    return (
      content.presentationPageNumber === presentationPageNumber &&
        content.documentPageNumber === page.number
    ) || content.documentPageNumber === page.number;
  });
}

export function getSlideRulePages<
  TPage extends {
    id?: string,
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  contentPages,
  appendMissingHiddenContentPages = true,
}: {
  pages: TPage[],
  contentPages?: HiddenSlideContentPage[],
  appendMissingHiddenContentPages?: boolean,
}): TPage[] {
  const slideRulePages = pages.map((presentablePage) => {
    const contentPage = findHiddenSlideContentPage(
      presentablePage.page,
      presentablePage.presentationPageNumber,
      contentPages,
    );

    return {
      ...presentablePage,
      page: getSlideRollDisplayPage({
        page: presentablePage.page,
        source: contentPage,
        includeHiddenSlides: true,
      }),
    };
  });

  if (!appendMissingHiddenContentPages) {
    return slideRulePages;
  }

  const templatePage = pages[0];
  const missingHiddenContentPages = contentPages?.filter(contentPage =>
    (contentPage.isHidden || contentPage.sourceIsHidden) &&
    !slideRulePages.some(({ page }) =>
      (!!contentPage.pageId && page.pageId === contentPage.pageId) ||
      page.number === contentPage.documentPageNumber,
    ),
  ) ?? [];

  if (!templatePage || !missingHiddenContentPages.length) {
    return slideRulePages;
  }

  const appendedPages = missingHiddenContentPages.map((contentPage) => ({
    ...templatePage,
    id: `${templatePage.id ?? 'slide-rule'}_${contentPage.documentPageNumber}`,
    presentationPageNumber: contentPage.presentationPageNumber ?? contentPage.documentPageNumber,
    page: {
      ...templatePage.page,
      pageId: contentPage.pageId ?? templatePage.page.pageId,
      number: contentPage.documentPageNumber,
      isHidden: !!contentPage.isHidden,
      sourceIsHidden: true,
    },
  } as TPage));

  return [...slideRulePages, ...appendedPages]
    .sort((a, b) => a.page.number - b.page.number);
}

export function getVisiblePresentationPages<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  contentPages,
}: {
  pages: TPage[],
  contentPages?: HiddenSlideContentPage[],
}): TPage[] {
  const slideRulePages = getSlideRulePages({
    pages,
    contentPages,
    appendMissingHiddenContentPages: false,
  });
  const visiblePages = slideRulePages.filter(({ page }) => !page.isHidden);

  return visiblePages;
}

export function getVisiblePresentationPageCounter<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  currentPresentationPageNumber,
}: {
  pages: TPage[],
  currentPresentationPageNumber: number,
}): {
  currentPageNumber: number,
  totalPages: number,
} {
  const visiblePages = pages.filter(({ page }) => !page.isHidden);
  const currentVisibleIndex = visiblePages
    .findIndex(page => page.presentationPageNumber === currentPresentationPageNumber);

  return {
    currentPageNumber: currentVisibleIndex >= 0
      ? currentVisibleIndex + 1
      : currentPresentationPageNumber,
    totalPages: visiblePages.length,
  };
}

export function shouldHidePresentationPageCounter<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  featureEnabled,
  pages,
  currentPresentationPageNumber,
}: {
  featureEnabled: boolean,
  pages: TPage[],
  currentPresentationPageNumber?: number,
}): boolean {
  if (!featureEnabled || typeof currentPresentationPageNumber !== 'number') {
    return false;
  }

  return !!pages.find(page =>
    page.presentationPageNumber === currentPresentationPageNumber &&
    !!page.page.isHidden,
  );
}

export function canDirectSelectPresentationPage({
  featureEnabled,
  page,
}: {
  featureEnabled: boolean,
  page: HiddenSlidePage,
}): boolean {
  return !page.isHidden || featureEnabled;
}

export function getNextVisiblePresentationPageNumber<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  contentPages,
  currentPresentationPageNumber,
}: {
  pages: TPage[],
  contentPages?: HiddenSlideContentPage[],
  currentPresentationPageNumber: number,
}): number | undefined {
  const visiblePages = getVisiblePresentationPages({
    pages,
    contentPages,
  });
  const currentVisibleIndex = visiblePages
    .findIndex(page => page.presentationPageNumber === currentPresentationPageNumber);

  if (currentVisibleIndex >= 0) {
    return visiblePages[currentVisibleIndex + 1]?.presentationPageNumber;
  }

  return visiblePages
    .find(page => page.presentationPageNumber > currentPresentationPageNumber)
    ?.presentationPageNumber;
}

export function getPreviousVisiblePresentationPageNumber<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  contentPages,
  currentPresentationPageNumber,
}: {
  pages: TPage[],
  contentPages?: HiddenSlideContentPage[],
  currentPresentationPageNumber: number,
}): number | undefined {
  const visiblePages = getVisiblePresentationPages({
    pages,
    contentPages,
  });
  const currentVisibleIndex = visiblePages
    .findIndex(page => page.presentationPageNumber === currentPresentationPageNumber);

  if (currentVisibleIndex >= 0) {
    return visiblePages[currentVisibleIndex - 1]?.presentationPageNumber;
  }

  return [...visiblePages]
    .reverse()
    .find(page => page.presentationPageNumber < currentPresentationPageNumber)
    ?.presentationPageNumber;
}

export function getPagesWithHiddenStateOverrides<
  TPage extends {
    page: HiddenSlidePage,
  },
>({
  pages,
  hiddenStateOverrides,
}: {
  pages: TPage[],
  hiddenStateOverrides: Record<string, boolean>,
}): TPage[] {
  return pages.map((presentablePage) => {
    const pageId = presentablePage.page.pageId;
    const hiddenOverride = pageId ? hiddenStateOverrides[pageId] : undefined;

    if (typeof hiddenOverride !== 'boolean') {
      return presentablePage;
    }

    return {
      ...presentablePage,
      page: {
        ...presentablePage.page,
        isHidden: hiddenOverride,
      },
    };
  });
}

export function getPageSettingOverridesByPageId(
  pageSettingOverrides: HiddenSlidePageSettingOverride[] = [],
): Record<string, boolean> {
  return pageSettingOverrides.reduce<Record<string, boolean>>((acc, pageSetting) => {
    if (!pageSetting.pageId || typeof pageSetting.hidden !== 'boolean') {
      return acc;
    }

    acc[pageSetting.pageId] = pageSetting.hidden;
    return acc;
  }, {});
}

export function getNormalizedPageSettingOverrides<
  TPageSetting extends HiddenSlidePageSettingOverride,
>(
  pageSettingOverrides: TPageSetting[] = [],
): HiddenSlidePageSettingOverride[] {
  return Object
    .entries(getPageSettingOverridesByPageId(pageSettingOverrides))
    .map(([pageId, hidden]) => ({ pageId, hidden }));
}

export function getHiddenSlideLocalOverrideKey({
  presentableId,
  documentPageNumber,
  presentationPageNumber,
}: {
  presentableId: string,
  documentPageNumber: number,
  presentationPageNumber?: number,
}): string {
  return `presentable:${presentableId}:document:${documentPageNumber}:presentation:${presentationPageNumber}`;
}

export function getHiddenSlideContentPageOverride({
  hiddenStateOverrides,
  persistedHiddenSlideStateOverrides,
  presentableId,
  page,
}: {
  hiddenStateOverrides: Record<string, boolean>,
  persistedHiddenSlideStateOverrides?: Record<string, boolean>,
  presentableId?: string,
  page: HiddenSlideContentPageOverrideTarget,
}): boolean | undefined {
  if (presentableId) {
    const scopedOverride = hiddenStateOverrides[getHiddenSlideLocalOverrideKey({
      presentableId,
      documentPageNumber: page.documentPageNumber,
      presentationPageNumber: page.presentationPageNumber,
    })];

    if (typeof scopedOverride === 'boolean') {
      return scopedOverride;
    }
  }

  return page.pageId ? persistedHiddenSlideStateOverrides?.[page.pageId] : undefined;
}

function getPageWithHiddenSlideOverride<TPage extends HiddenSlidePage>({
  page,
  pageSettingOverrides,
}: {
  page: TPage,
  pageSettingOverrides?: HiddenSlidePageSettingOverride[],
}): TPage {
  const hiddenOverride = page.pageId
    ? getPageSettingOverridesByPageId(pageSettingOverrides)[page.pageId]
    : undefined;

  if (typeof hiddenOverride !== 'boolean') {
    return page;
  }

  return {
    ...page,
    isHidden: hiddenOverride,
  };
}

export function getPageSettingsWithHiddenOverrideToggle<
  TPageSetting extends HiddenSlidePageSettingOverride,
>({
  pageSettings,
  pageId,
  nextIsHidden,
}: {
  pageSettings: TPageSetting[],
  pageId: string,
  nextIsHidden: boolean,
}): HiddenSlidePageSettingOverride[] {
  const normalizedPageSettings = getNormalizedPageSettingOverrides(pageSettings);
  const hasExistingSetting = normalizedPageSettings.some(pageSetting => pageSetting.pageId === pageId);

  if (!hasExistingSetting) {
    return [
      ...normalizedPageSettings,
      {
        pageId,
        hidden: nextIsHidden,
      },
    ];
  }

  return normalizedPageSettings.map(pageSetting => {
    if (pageSetting.pageId !== pageId) {
      return pageSetting;
    }

    return {
      ...pageSetting,
      hidden: nextIsHidden,
    };
  });
}

export function getPageWithHiddenSlideSettingsState<TPage extends HiddenSlidePage>({
  page,
  documentVersionPageSettings,
  documentVersionPageSettingOverrides,
  customDeckPageSettingOverrides,
}: {
  page: TPage,
  documentVersionPageSettings?: HiddenSlidePageSetting[],
  documentVersionPageSettingOverrides?: HiddenSlidePageSettingOverride[],
  customDeckPageSettingOverrides?: HiddenSlidePageSettingOverride[],
}): TPage {
  if (customDeckPageSettingOverrides) {
    // Custom decks store current visibility, while sourceIsHidden still comes from the document version.
    const pageWithSourceState = getPageWithPageSettingsHiddenState({
      page,
      pageSettings: documentVersionPageSettings ?? [],
    });
    const customDeckHiddenOverride = page.pageId
      ? getPageSettingOverridesByPageId(customDeckPageSettingOverrides)[page.pageId]
      : undefined;

    return {
      ...pageWithSourceState,
      isHidden: !!customDeckHiddenOverride,
      sourceIsHidden: !!pageWithSourceState.sourceIsHidden,
    };
  }

  const pageWithSourceState = getPageWithPageSettingsHiddenState({
    page,
    pageSettings: documentVersionPageSettings ?? [],
  });
  const pageWithDocumentVersionOverride = getPageWithHiddenSlideOverride({
    page: pageWithSourceState,
    pageSettingOverrides: documentVersionPageSettingOverrides,
  });

  return getPageWithHiddenSlideOverride({
    page: pageWithDocumentVersionOverride,
    pageSettingOverrides: customDeckPageSettingOverrides,
  });
}

export function getCustomDeckInheritedPageSettingOverrides({
  pages,
  documentVersionPageSettings,
  documentVersionPageSettingOverrides,
}: {
  pages: HiddenSlidePage[],
  documentVersionPageSettings: HiddenSlidePageSetting[],
  documentVersionPageSettingOverrides?: HiddenSlidePageSettingOverride[],
}): HiddenSlidePageSettingOverride[] {
  return getNormalizedPageSettingOverrides(
    pages.reduce<HiddenSlidePageSettingOverride[]>((acc, page) => {
      const sourcePage = getPageWithPageSettingsHiddenState({
        page,
        pageSettings: documentVersionPageSettings,
      });
      const effectivePage = getPageWithHiddenSlideOverride({
        page: sourcePage,
        pageSettingOverrides: documentVersionPageSettingOverrides,
      });
      const hasDocumentVersionOverride = !!page.pageId &&
        typeof getPageSettingOverridesByPageId(documentVersionPageSettingOverrides)[page.pageId] === 'boolean';

      if (!page.pageId || (!isSourceHiddenSlide(sourcePage) && !hasDocumentVersionOverride)) {
        return acc;
      }

      acc.push({
        pageId: page.pageId,
        hidden: !!effectivePage.isHidden,
      });

      return acc;
    }, []),
  );
}

export function getMergedCustomDeckPageSettingOverrides({
  inheritedPageSettings,
  existingCustomDeckPageSettings,
}: {
  inheritedPageSettings: HiddenSlidePageSettingOverride[],
  existingCustomDeckPageSettings?: HiddenSlidePageSettingOverride[],
}): HiddenSlidePageSettingOverride[] {
  return getNormalizedPageSettingOverrides([
    ...inheritedPageSettings,
    ...(existingCustomDeckPageSettings ?? []),
  ]);
}

export function getCustomDeckCurrentPageSettingOverrides({
  groups,
}: {
  groups: Array<{
    id?: string,
    visible?: boolean | null,
    pages?: HiddenSlidePage[],
  }>,
}): HiddenSlidePageSettingOverride[] {
  return getNormalizedPageSettingOverrides(
    groups.flatMap(group => (
      group.pages
        ?.filter(page => page.pageId && isSourceHiddenSlide(page))
        .map(page => ({
          pageId: page.pageId!,
          hidden: group.visible === false || page.isHidden === true,
        })) ?? []
    )),
  );
}

export function canHidePresentationPage<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  pageToHide,
}: {
  pages: TPage[],
  pageToHide: TPage,
}): boolean {
  if (pageToHide.page.isHidden) {
    return true;
  }

  return pages.filter(({ page }) => !page.isHidden).length > 1;
}

export function getFallbackPresentationPageNumberAfterHide<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  hiddenPresentationPageNumber,
}: {
  pages: TPage[],
  hiddenPresentationPageNumber: number,
}): number | undefined {
  const hiddenPageIndex = pages.findIndex(page => page.presentationPageNumber === hiddenPresentationPageNumber);
  if (hiddenPageIndex === -1) {
    return undefined;
  }

  const nextVisiblePage = pages.slice(hiddenPageIndex + 1).find(({ page }) => !page.isHidden);
  if (nextVisiblePage) {
    return nextVisiblePage.presentationPageNumber;
  }

  return pages.slice(0, hiddenPageIndex).reverse().find(({ page }) => !page.isHidden)?.presentationPageNumber;
}

export function getPresentationModePages<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  contentPages,
  includeHiddenSlides,
}: {
  pages: TPage[],
  contentPages?: HiddenSlideContentPage[],
  includeHiddenSlides?: boolean,
}): TPage[] {
  if (includeHiddenSlides) {
    return getSlideRulePages({
      pages,
      contentPages,
    });
  }

  return getVisiblePresentationPages({
    pages,
    contentPages,
  });
}

export function getPresentationModeFallbackPageNumber<
  TPage extends {
    presentationPageNumber: number,
    page: HiddenSlidePage,
  },
>({
  pages,
  contentPages,
  currentPresentationPageNumber,
  includeHiddenSlides,
}: {
  pages: TPage[],
  contentPages?: HiddenSlideContentPage[],
  currentPresentationPageNumber: number,
  includeHiddenSlides?: boolean,
}): number | undefined {
  const visiblePages = getPresentationModePages({
    pages,
    contentPages,
    includeHiddenSlides,
  });
  const currentPage = visiblePages.find(page => page.presentationPageNumber === currentPresentationPageNumber);

  return currentPage?.presentationPageNumber ?? visiblePages[0]?.presentationPageNumber;
}

export function getContentPageWithPageSettingsHiddenState<TPage extends HiddenSlideContentPage>({
  page,
  pageSettings,
}: {
  page: TPage,
  pageSettings: HiddenSlidePageSetting[],
}): TPage {
  const pageSettingsMap = getHiddenPageSettingsByPageNumber(pageSettings);
  const setting = pageSettingsMap.get(page.documentPageNumber);

  if (!setting) {
    return {
      ...page,
      isHidden: false,
      sourceIsHidden: false,
    };
  }

  return {
    ...page,
    isHidden: setting.isHidden ?? page.isHidden,
    sourceIsHidden: setting.sourceIsHidden ?? page.sourceIsHidden,
  };
}

export function getPageWithPageSettingsHiddenState<TPage extends HiddenSlidePage>({
  page,
  pageSettings,
}: {
  page: TPage,
  pageSettings: HiddenSlidePageSetting[],
}): TPage {
  const pageWithHiddenState = getContentPageWithPageSettingsHiddenState({
    page: {
      pageId: page.pageId,
      documentPageNumber: page.number,
      presentationPageNumber: page.number,
      isHidden: page.isHidden,
      sourceIsHidden: page.sourceIsHidden,
    },
    pageSettings,
  });

  return {
    ...page,
    isHidden: pageWithHiddenState.isHidden,
    sourceIsHidden: pageWithHiddenState.sourceIsHidden,
  };
}

function getHiddenPageSettingsByPageNumber(
  pageSettings: HiddenSlidePageSetting[],
): Map<number, HiddenSlidePageSetting> {
  const settingsByPageNumber = pageSettings.reduce((acc, pageSetting) => {
    const existingSettings = acc.get(pageSetting.number) ?? [];
    acc.set(pageSetting.number, [...existingSettings, pageSetting]);
    return acc;
  }, new Map<number, HiddenSlidePageSetting[]>());

  return Array.from(settingsByPageNumber.entries()).reduce((acc, [pageNumber, settings]) => {
    acc.set(pageNumber, mergeHiddenPageSettings(settings));
    return acc;
  }, new Map<number, HiddenSlidePageSetting>());
}

function mergeHiddenPageSettings(pageSettings: HiddenSlidePageSetting[]): HiddenSlidePageSetting {
  const fallbackSetting = pageSettings[pageSettings.length - 1];
  // Prefer explicit hidden metadata; old records only used isHidden to imply source-hidden.
  const explicitHiddenSettings = pageSettings.filter(pageSetting =>
    typeof pageSetting.sourceIsHidden === 'boolean' ||
    typeof pageSetting.isHidden === 'boolean');
  const latestExplicitHiddenSetting =
    explicitHiddenSettings[explicitHiddenSettings.length - 1];
  const hasLegacyHiddenSetting = pageSettings.some(pageSetting => !!pageSetting.isHidden);
  const sourceIsHidden = latestExplicitHiddenSetting
    ? !!latestExplicitHiddenSetting.sourceIsHidden
    : hasLegacyHiddenSetting;
  const isHidden = latestExplicitHiddenSetting
    ? !!latestExplicitHiddenSetting.isHidden
    : hasLegacyHiddenSetting;

  return {
    ...fallbackSetting,
    isRequired: pageSettings.some(pageSetting => !!pageSetting.isRequired),
    isHidden,
    sourceIsHidden,
    linkedSlides: fallbackSetting.linkedSlides ?? [],
  };
}

export function getCustomDeckDefaultGroupVisibility(pages: HiddenSlidePage[]): boolean {
  return !pages.every(page => !!page.sourceIsHidden && !!page.isHidden);
}
